import { ViewportObserver } from "./renderer/web-components/viewport";
import * as AST from "./parse/sfml-ast";
import * as E from "./renderer/web-components/element";
import * as L from "./renderer/web-components/layout";
import * as R from "./renderer/web-components";
import { AssertionError } from "assert";

function getUIElementMarginWidth(element: AST.UIElementBase): number {
  let margin = 0;

  if (element.margin) {
    margin += element.margin.left;
    margin += element.margin.right;
  }

  return margin;
}

function toRGBStyle(color: AST.Color) {
  return `rgb(${color.r * 255}, \
  ${color.g * 255}, ${color.b * 255})`;
}

function getUIElementMarginHeight(element: AST.UIElementBase): number {
  let margin = 0;

  if (element.margin) {
    margin += element.margin.top;
    margin += element.margin.bottom;
  }

  return margin;
}

function adjustImageHeight(image: R.FlyerImage | R.UrlImage, flexGrow: number, orientation: string): void {
  image.setAttribute("aspect-ratio", (1 / (flexGrow || 1)).toString());
  E.AspectResizer.shared.register(image, "height", orientation);
}

function applyUIElementBase(element: AST.UIElementBase, target: HTMLElement, style?: AST.StyleElement | null) {
  const aspectRatio = element.aspectRatio;
  if (aspectRatio) target.setAttribute("aspect-ratio", aspectRatio.toString());

  const widthMargin = getUIElementMarginWidth(element);

  const width = element.width;
  const orientation = target.getAttribute("orientation") || "vertical";
  if (width === "match-parent" && widthMargin === 0) target.style.width = "100%";
  else if (width === "match-parent") target.style.width = `calc(100% - ${widthMargin}px)`;
  else if (width === "wrap-content") target.style.width = "auto";
  else if (width === "preserve-aspect") E.AspectResizer.shared.register(target, "width", orientation);
  else target.style.width = width + "px";

  const heightMargin = getUIElementMarginHeight(element);

  const height = element.height;
  if (height === "match-parent" && heightMargin === 0) target.style.height = "100%";
  else if (height === "match-parent") target.style.height = `calc(100% - ${heightMargin}px)`;
  else if (height === "wrap-content") target.style.height = "auto";
  else if (height === "preserve-aspect") E.AspectResizer.shared.register(target, "height", orientation);
  else target.style.height = height + "px";

  const backgroundColor = element.backgroundColor;
  if (backgroundColor) {
    target.style.backgroundColor = `rgb(${backgroundColor.r * 255}, \
${backgroundColor.g * 255}, ${backgroundColor.b * 255})`;
  }

  if (element.margin) {
    target.style.margin = `${element.margin.top}\px \
${element.margin.right}px ${element.margin.bottom}px ${element.margin.left}px`;
  }

  if (element.padding) {
    target.style.padding = `${element.padding.top}px ${element.padding.right}\
px ${element.padding.bottom}px ${element.padding.left}px`;
  }

  if (element.borderColor) {
    const borderColor = element.borderColor;
    target.style.borderColor = `rgb(${borderColor.r * 255}, \
${borderColor.g * 255}, ${borderColor.b * 255})`;
  }

  if (element.borderWidth) {
    const borderWidth = element.borderWidth;
    target.style.borderWidth = `${borderWidth.top}px ${borderWidth.right}\
px ${borderWidth.bottom}px ${borderWidth.left}px`;
    target.style.borderStyle = "solid";
  }

  const anchorId = element.anchorId;
  if (anchorId) {
    const anchor = document.createElement("a");
    anchor.setAttribute("wayfinder-anchor", "");
    anchor.setAttribute("name", `sfml_anchor_${anchorId}`);
    target.insertBefore(anchor, target.firstChild);
    target.setAttribute("sfml-anchor-id", anchorId);
  }

  const styleId = element.styleId;
  if (styleId && style) {
    target.setAttribute("style-id", styleId.toString());

    /**
     * NOTE: itemAtomStyle is compiled into CSS and is automatically applied when
     *       style is matched.
     *
     *       Somehow the style-id attribute cannot be selected by the selector.
     *       Therefore we construct a class that can be referenced by CSS.
     */
    target.className = `style-id-${styleId} ${target.className}`;

    const matchedStyle = style!.styles.find((style) => style.id === styleId);
    if (matchedStyle !== undefined) {
      appendStyle(target, matchedStyle!);
    }
  }
}

function appendStyle(targetElement: HTMLElement, style: AST.StyleTypeElement) {
  if (style.paddingStyle!.padding) {
    targetElement.style.padding = `${style.paddingStyle!.padding.top}px ${style.paddingStyle!.padding.right}px
      ${style.paddingStyle!.padding.bottom}px ${style.paddingStyle!.padding.left}px`;
  }
  if (style.marginStyle!.margin) {
    targetElement.style.margin = `${style.marginStyle!.margin.top}px ${style.marginStyle!.margin.right}px
      ${style.marginStyle!.margin.bottom}px ${style.marginStyle!.margin.left}px`;
    const horizontalMargin = style.marginStyle!.margin.right + style.marginStyle!.margin.left;
    targetElement.style.width = `calc(${targetElement.style.width} - ${horizontalMargin}px)`;
  }
  // If both background-color and -url are set we will take the url and drop color
  if (style.backgroundStyle) {
    const { url, color } = style.backgroundStyle;
    if (url) {
      targetElement.style.backgroundImage = `url(${url})`;
      targetElement.style.backgroundSize = "100% 100%";
      targetElement.style.backgroundRepeat = "no-repeat";
    } else if (color) {
      targetElement.style.backgroundColor = toRGBStyle(color);
    }
  }

  if (style.borderStyle) {
    applyBorderStyle("left", targetElement, style.borderStyle.left);
    applyBorderStyle("right", targetElement, style.borderStyle.right);
    applyBorderStyle("top", targetElement, style.borderStyle.top);
    applyBorderStyle("bottom", targetElement, style.borderStyle.bottom);
  }

  if (style.textStyle) {
    const { fontFamily, fontWeight, fontColor, fontSize } = style.textStyle;
    if (fontFamily) targetElement.style.fontFamily = fontFamily.toString();
    if (fontWeight) targetElement.style.fontWeight = fontWeight.toString();
    if (fontColor) targetElement.style.color = toRGBStyle(fontColor);
    if (fontSize) targetElement.style.fontSize = `${fontSize.toString()}px`;
  }
}

function applyBorderStyle(
  side: "left" | "right" | "top" | "bottom",
  targetElement: HTMLElement,
  style: AST.BorderStyle | null,
) {
  if (!style) {
    return;
  }

  switch (side) {
    case "left":
      targetElement.style.borderLeftColor = style.color;
      targetElement.style.borderLeftWidth = `${style.width}px`;
      targetElement.style.borderLeftStyle = style.style;
      break;
    case "right":
      targetElement.style.borderRightColor = style.color;
      targetElement.style.borderRightWidth = `${style.width}px`;
      targetElement.style.borderRightStyle = style.style;
      break;
    case "top":
      targetElement.style.borderTopColor = style.color;
      targetElement.style.borderTopWidth = `${style.width}px`;
      targetElement.style.borderTopStyle = style.style;
      break;
    case "bottom":
      targetElement.style.borderBottomColor = style.color;
      targetElement.style.borderBottomWidth = `${style.width}px`;
      targetElement.style.borderBottomStyle = style.style;
      break;
  }
}

function appendStorefront(element: AST.StorefrontElement, wayfinder: (R.WayfinderControl & R.SFMLElement) | null) {
  const storefront = new R.Storefront();
  if (element.title) storefront.setAttribute("title", element.title);

  if (element.subtitle) storefront.setAttribute("subtitle", element.subtitle);

  if (element.uuid) storefront.setAttribute("sfmlUUID", element.uuid);

  if (element.wayfinder && wayfinder) {
    appendWayfinder(element.wayfinder, wayfinder, storefront);
    storefront.appendChild(wayfinder);
  }

  const wrap = document.createElement("div");
  wrap.setAttribute("sfml-content-wrap", "");
  storefront.appendChild(wrap);

  appendUIElement(element.content, wrap, element.style);
  return storefront;
}

function appendScrollView(element: AST.ScrollViewElement, parent: HTMLElement, style?: AST.StyleElement | null) {
  const scrollView = new R.ScrollView();
  scrollView.setAttribute("orientation", element.orientation);
  appendUIElement(element.content, scrollView, style);
  return scrollView;
}

function appendLinearLayout(element: AST.LinearLayoutElement, parent: HTMLElement, style?: AST.StyleElement | null) {
  const linearLayout = new R.LinearLayout();
  linearLayout.setAttribute("orientation", element.orientation);
  for (let content of element.content) {
    appendUIElement(content, linearLayout, style);
  }

  return linearLayout;
}

function appendCollapsibleLayout(
  element: AST.CollapsibleLayoutElement,
  parent: HTMLElement,
  style?: AST.StyleElement | null,
) {
  const collapsibleLayout = new R.CollapsibleLayout();
  collapsibleLayout.setAttribute("orientation", element.orientation);

  collapsibleLayout.setAttribute("title", element.title);
  collapsibleLayout.setAttribute("exposed-title", element.exposedTitle);
  collapsibleLayout.setAttribute("collapse-type", element.collapseType);

  if (element.icon) collapsibleLayout.setAttribute("icon", element.icon);

  if (element.iconUrl) collapsibleLayout.setAttribute("icon-url", element.iconUrl);

  for (let content of element.content) appendUIElement(content, collapsibleLayout, style);

  return collapsibleLayout;
}

function appendFlexbox(element: AST.FlexboxElement, parent: HTMLElement, style?: AST.StyleElement | null) {
  const flexbox = new R.Flexbox();
  flexbox.setAttribute("orientation", element.orientation);

  for (let content of element.content) appendUIElement(content, flexbox, style);

  // Only assign flex weights if child elements all created single children.
  if (flexbox.children.length !== element.content.length) return flexbox;

  for (let i = 0; i < element.content.length; i++) {
    const child = flexbox.children[i];
    if (child instanceof HTMLElement) {
      child.style.flex = element.content[i].attachedProperties["flex-grow"] || "1";
    }
  }

  return flexbox;
}

function appendBadge(element: AST.BadgeElement, parent: HTMLElement) {
  const badge = new R.Badge();

  badge.style.display = "block";
  badge.style.position = "absolute";
  badge.style.width = `${element.width}px`;
  badge.style.height = `${element.height}px`;
  badge.style.left = `${(element.hpos - 0) * 100}%`;
  badge.style.top = `${(element.vpos - 0) * 100}%`;

  const xTranslation = `${(element.x - 0) * 100}%`;
  const yTranslation = `${(element.y - 0) * 100}%`;
  badge.style.webkitTransform = `translate(-${xTranslation}, -${yTranslation})`;

  badge.style.zIndex = "99";

  badge.setAttribute("url", element.url.toString());
  badge.setAttribute("hpos", element.hpos.toString());
  badge.setAttribute("vpos", element.vpos.toString());

  return badge;
}

function appendItemAtom(element: AST.ItemAtomElement, parent: HTMLElement, style?: AST.StyleElement | null) {
  const itemAtom = new R.ItemAtom();
  const imagePosition = element.imagePosition.toString();
  itemAtom.setAttribute("width", element.width.toString());
  itemAtom.setAttribute("height", element.height.toString());
  itemAtom.setAttribute("item-type", element.itemType.toString());
  itemAtom.setAttribute("source-id", element.itemId.toString());

  itemAtom.setAttribute("image-position", imagePosition);

  if (element.flexGrow) {
    itemAtom.setAttribute("flex-grow", element.flexGrow.toString());
  }

  if (element.targetAnchorId) itemAtom.setAttribute("target-anchor-id", element.targetAnchorId);

  if (element.payload) {
    for (const [key, value] of Array.from(element.payload.entries())) {
      itemAtom.setAttribute(AST.PAYLOAD_PREFIX + key, value);
    }
  }

  if (element.borderWidth) itemAtom.setAttribute("bordered", "true");

  const image = new R.UrlImage();

  image.setAttribute("url", element.imageUrl.toString());
  image.setAttribute("width", element.imageWidth.toString());
  image.setAttribute("height", element.imageHeight.toString());

  itemAtom.appendChild(image);
  adjustImageHeight(image, element.flexGrow || 1, imagePosition);

  for (let badge of element.badges) {
    const badgeElement = appendBadge(badge, itemAtom);
    itemAtom.appendChild(badgeElement);
  }

  for (let text of element.texts) {
    const textView = appendText(text);
    if (style !== null && text.styleId !== null) {
      const textStyle = style!.styles.find((element) => {
        return element.id === text.styleId;
      });
      if (textStyle !== undefined) {
        appendStyle(textView, textStyle);
      }
      textView.style.setProperty("-webkit-line-clamp", text.maxLines.toString());
    }
    itemAtom.insertTextElement(textView);
  }

  return itemAtom;
}

function appendText(textElement: AST.TextElement): L.TextView {
  let textView = new L.TextView();
  textView.value = textElement.value;
  textView.maxLines = textElement.maxLines;

  return textView;
}

function appendAreaAttributes(area: AST.AreaElement, anchor: any) {
  const rect = area.rect;

  if (area.itemType) {
    anchor.setAttribute("item-type", area.itemType);
  }

  if (area.targetAnchorId) {
    anchor.setAttribute("target-anchor-id", area.targetAnchorId);
  }

  if (area.itemId) {
    anchor.setAttribute("source-id", `${area.itemId}`);
  }

  if (area.payload) {
    for (const [key, value] of Array.from(area.payload.entries())) {
      anchor.setAttribute(AST.PAYLOAD_PREFIX + key, value);
    }
  }

  if (area.action && area.action.type === AST.Action.LINK) {
    if (!area.action.ttmUrl) {
      throw Error("a ttm url must be given for action type LINK!");
    }

    anchor.setAttribute("action-type", AST.Action.LINK);
    anchor.setAttribute("ttm-url", area.action.ttmUrl);
  }

  anchor.setAttribute("href", area.href);
  anchor.setAttribute("src-rect", `${rect.left} ${rect.top} ${rect.width} ${rect.height}`);
  anchor.setAttribute("label", area.label);
}

function appendSource(source: AST.FlyerSourceElement | AST.UrlSourceElement, parent: HTMLElement) {
  if (source.format === "tiled") {
    const image = new R.FlyerImage();
    image.setAttribute("width", source.width.toString());
    image.setAttribute("height", source.height.toString());

    image.setAttribute("path", source.path);
    image.setAttribute("resolutions", source.resolutions.join(" "));

    const rect = source.rect;
    image.setAttribute("src-rect", `${rect.left} ${rect.top} ${rect.width} ${rect.height}`);

    for (let area of source.areas) {
      const anchor = new R.FlyerImageAnchor();
      appendAreaAttributes(area, anchor);

      image.appendChild(anchor);
    }

    return image;
  } else if (source.format === "url") {
    const image = new R.UrlImage();

    image.setAttribute("url", source.url);
    image.setAttribute("width", source.width.toString());
    image.setAttribute("height", source.height.toString());

    for (let area of source.areas) {
      const anchor = new R.UrlImageAnchor();
      appendAreaAttributes(area, anchor);

      image.appendChild(anchor);
    }

    return image;
  } else {
    throw "Only tiled sources supported";
  }
}

function appendImage(element: AST.ImageElement, parent: HTMLElement) {
  const source = element.sources[0];
  if (!source) throw "No sources found";
  const orientation = parent.getAttribute("orientation") || "vertical";
  const imageElement = appendSource(source, parent);
  adjustImageHeight(imageElement, element.flexGrow || 1, orientation);

  for (let badge of element.badges) {
    const badgeElement = appendBadge(badge, imageElement);
    imageElement.appendChild(badgeElement);
  }

  return imageElement;
}

function appendUIElement(element: AST.UIElement, parent: HTMLElement, style?: AST.StyleElement | null): HTMLElement {
  switch (element.kind) {
    case "storefront": {
      return parent;
    }
    case "scrollview": {
      const scrollView = appendScrollView(element, parent, style);
      applyUIElementBase(element, scrollView, style);
      return parent.appendChild(scrollView);
    }
    case "linear-layout": {
      const linearLayout = appendLinearLayout(element, parent, style);
      applyUIElementBase(element, linearLayout, style);
      return parent.appendChild(linearLayout);
    }
    case "collapsible-layout": {
      const collapsibleLayout = appendCollapsibleLayout(element, parent, style);
      applyUIElementBase(element, collapsibleLayout, style);
      return parent.appendChild(collapsibleLayout);
    }
    case "flexbox": {
      const flexbox = appendFlexbox(element, parent, style);
      applyUIElementBase(element, flexbox, style);
      return parent.appendChild(flexbox);
    }
    case "item-atom": {
      const itemAtom = appendItemAtom(element, parent, style);
      applyUIElementBase(element, itemAtom, style);
      return parent.appendChild(itemAtom);
    }
    case "image": {
      const image = appendImage(element, parent);
      applyUIElementBase(element, image, style);
      return parent.appendChild(image);
    }
    case "wayfinder": {
      const wayfinder = new R.Wayfinder();
      appendWayfinder(element, wayfinder, parent);
      return parent.appendChild(wayfinder);
    }
    case "category": {
      const category = appendCategory(element, parent);
      return parent.appendChild(category);
    }
    case "badge": {
      const badge = appendBadge(element, parent);
      return parent.appendChild(badge);
    }
  }
}

function appendWayfinder(
  element: AST.WayfinderElement,
  wayfinder: R.WayfinderControl & R.SFMLElement,
  parent: HTMLElement,
) {
  for (let category of element.categories) {
    const categoryElement = appendCategory(category, wayfinder);
    wayfinder.appendCategory(categoryElement);
  }
}

function appendCategory(element: AST.WayfinderCategoryElement, parent: HTMLElement) {
  const category = new R.WayfinderCategory();

  category.setAttribute("sfml-anchor-id", element.anchorId);
  category.setAttribute("anchor-id", `sfml_anchor_${element.anchorId}`);
  category.label.textContent = element.name;

  return category;
}

export interface StorefrontController {
  setStorefront(storefront: R.Storefront): void;
}

function evaluateStorefront(
  storefrontAST: AST.StorefrontElement,
  controller: StorefrontController,
  wayfinder: R.WayfinderControl & R.SFMLElement,
  root: HTMLElement,
): R.Storefront {
  const storefront = appendStorefront(storefrontAST, wayfinder);

  controller.setStorefront(storefront);
  root.appendChild(storefront);

  E.AspectResizer.shared.resize();
  ViewportObserver.shared.ready(storefront);

  return storefront;
}

export default evaluateStorefront;



// WEBPACK FOOTER //
// webpack://SFMLWeb/src/evaluate.ts